import React, { useState, useMemo, useEffect } from "react";
import { useFustContext } from "../../hooks/useFustContext";
import "./TransportFust.css";
import TransportNavbar from "../../components/TransportNavbar";
import axios from "axios";

const API = process.env.REACT_APP_API_URL;

const TransportFust = () => {
  const { fustCompanies, loading, error } = useFustContext();
  const [expandedRegion, setExpandedRegion] = useState(null);
  // ✅ per regio precies 1 open company
  const [expandedCompanyByRegion, setExpandedCompanyByRegion] = useState({});

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalCompany, setModalCompany] = useState(null);
  const [modalSelectedCode, setModalSelectedCode] = useState(null); // shipping code
  const [formValues, setFormValues] = useState({ DC: "", Pallet: "", Stapel: "", Wpkkar: "" });
  const [submitting, setSubmitting] = useState(false);

  const [pendingTotals, setPendingTotals] = useState(null);
  const [pendingLoading, setPendingLoading] = useState(false);

  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const groupedCompanies = useMemo(() => {
    if (!Array.isArray(fustCompanies)) return {};
  
    const acc = {};
  
    const hasFust = (location) =>
      Number(location?.DC || 0) !== 0 ||
      Number(location?.Pallet || 0) !== 0 ||
      Number(location?.Stapel || 0) !== 0 ||
      Number(location?.Wpkkar || 0) !== 0;
  
    const addToRegion = (region, location) => {
      const regionKey = region || "Onbekend";
      (acc[regionKey] ??= []).push(location);
    };
  
    for (const company of fustCompanies) {
  
      // 1. Moederbedrijf als zelfstandige locatie
if (hasFust(company)) {
  addToRegion(company.Territory_Code || company.MAW_TerritoryCode, {
    ...company,

    _locationKey: `company-${company.Companyid}`,
    _locationType: "company",

    ShippingAddresses: [],
  });
}

// 2. Shipping addresses als zelfstandige locaties
if (Array.isArray(company.ShippingAddresses)) {
  for (const addr of company.ShippingAddresses) {

    if (!hasFust(addr)) continue;

    addToRegion(addr.Territory_Code || addr.MAW_TerritoryCode, {
      ...company,

      Territory_Code:
        addr.Territory_Code || addr.MAW_TerritoryCode || "Onbekend",

      Companyid: company.Companyid,
      _companyName: company.Name,

      Name: addr.Name || company.Name,
      Address: addr.Address,
      City: addr.City,

      // Bewaar naam losadres voor eventueel detailgebruik
      _shippingName: addr.Name,

      DC: Number(addr.DC || 0),
      Pallet: Number(addr.Pallet || 0),
      Stapel: Number(addr.Stapel || 0),
      Wpkkar: Number(addr.Wpkkar || 0),

      _locationKey: `shipping-${company.Companyid}-${addr.Code}`,
      _locationType: "shipping",
      _shippingCode: addr.Code,

      ShippingAddresses: [addr],
    });
  }
}
    }
  
    // Sorteer locaties binnen de regio
    for (const region of Object.keys(acc)) {
      acc[region].sort((a, b) =>
        (a.Name || "").localeCompare(b.Name || "")
      );
    }
  
    return acc;
  }, [fustCompanies]);

  console.log("GROUPED FUST LOCATIONS:", groupedCompanies);

  const toggleRegion = (region) => {
    setExpandedRegion((prevRegion) => (prevRegion === region ? null : region));
  };

  // ✅ toggle per company binnen een regio (single-open)
  const toggleCompany = (region, companyId) => {
    setExpandedCompanyByRegion((prev) => ({
      ...prev,
      [region]: prev[region] === companyId ? null : companyId,
    }));
  };

  // 🔽 open modal voor terugboeken (nu: pending indienen)
  const openReturnModal = (company) => {
    setModalCompany(company);

    // kies default shipping code (als aanwezig: eerste met fust ≠ 0, anders eerste)
    const hasShipping = Array.isArray(company.ShippingAddresses) && company.ShippingAddresses.length > 0;
    if (hasShipping) {
      const nonZero = company.ShippingAddresses.find(
        (a) => a.DC !== 0 || a.Pallet !== 0 || a.Stapel !== 0 || a.Wpkkar !== 0
      );
      setModalSelectedCode((nonZero || company.ShippingAddresses[0]).Code);
    } else {
      setModalSelectedCode(null);
    }

    // reset invoer
    setFormValues({ DC: "", Pallet: "", Stapel: "", Wpkkar: "" });

    setSelectedPhoto(null);
    setPhotoPreview(null);

    setIsModalOpen(true);
  };

  // 🔽 bereken actuele saldo (bedrijf of gekozen shipping address)
  const currentBalance = useMemo(() => {
    if (!modalCompany) return { DC: 0, Pallet: 0, Stapel: 0, Wpkkar: 0 };
    const hasShipping = Array.isArray(modalCompany.ShippingAddresses) && modalCompany.ShippingAddresses.length > 0;

    if (!hasShipping) {
      return {
        DC: Number(modalCompany.DC || 0),
        Pallet: Number(modalCompany.Pallet || 0),
        Stapel: Number(modalCompany.Stapel || 0),
        Wpkkar: Number(modalCompany.Wpkkar || 0),
      };
    }
    const addr =
      modalCompany.ShippingAddresses.find((a) => a.Code === modalSelectedCode) ||
      modalCompany.ShippingAddresses[0];
    return {
      DC: Number(addr.DC || 0),
      Pallet: Number(addr.Pallet || 0),
      Stapel: Number(addr.Stapel || 0),
      Wpkkar: Number(addr.Wpkkar || 0),
    };
  }, [modalCompany, modalSelectedCode]);

  useEffect(() => {
    // als er geen modal open is: geen pending
    if (!modalCompany) {
      setPendingTotals(null);
      return;
    }
  
    const source = axios.CancelToken.source();
  
    const fetchPending = async () => {
      try {
        setPendingLoading(true);
  
        const params = new URLSearchParams();
        params.append("companyId", modalCompany.Companyid);
  
        // shippingCode meegeven als je op een shipping address zit
        if (modalSelectedCode) {
          params.append("shippingCode", modalSelectedCode);
        }
  
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/fust/pending/by-location?${params.toString()}`,
          { cancelToken: source.token }
        );
  
        setPendingTotals(res.data?.totals || null);
      } catch (e) {
        if (!axios.isCancel(e)) {
          console.error("❌ Pending ophalen mislukt:", e?.response?.data || e.message);
        }
      } finally {
        setPendingLoading(false);
      }
    };
  
    fetchPending();
  
    return () => {
      source.cancel("Component unmounted of modal gesloten");
    };
  }, [modalCompany, modalSelectedCode]);

  const onChangeQty = (key, value) => {
    if (value === "") {
      setFormValues((prev) => ({ ...prev, [key]: "" }));
      return;
    }
  
    const n = Number(value);
    if (Number.isNaN(n)) return;
  
    setFormValues((prev) => ({ ...prev, [key]: Math.trunc(n) }));
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalCompany(null);
    setModalSelectedCode(null);
    setSubmitting(false);

    setSelectedPhoto(null);
    setPhotoPreview(null);
  };

  const onPhotoSelect = (file) => {
    if (!file) return;
  
    setSelectedPhoto(file);
  
    // preview maken
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // ✅ INDIENEN NAAR PENDING
  const confirmReturn = async () => {
    if (!modalCompany) return;
  
    // check: is er minstens één waarde ≠ 0?
    const entries = Object.entries(formValues).map(([key, value]) => [
      key,
      value === "" ? 0 : Number(value) || 0,
    ]);
  
    const hasNonZero = entries.some(([_, v]) => v !== 0);
  
    if (!hasNonZero) {
      alert("Vul minimaal één hoeveelheid ≠ 0 in.");
      return;
    }
  
    // optioneel naam van chauffeur uit localStorage
    const transportUser = JSON.parse(localStorage.getItem("transportUser") || "null");
    const driverName =
      transportUser?.username ||
      transportUser?.name ||
      transportUser?.company ||
      "onbekend";
  
    // ✅ FormData i.p.v. JSON
    const fd = new FormData();
    fd.append("companyId", modalCompany.Companyid);
    fd.append("shippingCode", modalSelectedCode || ""); // leeg = hoofdbedrijf
    fd.append("driverName", driverName);
  
    // return object als JSON-string (backend parse’t dit)
    fd.append(
      "return",
      JSON.stringify({
        DC: formValues.DC === "" ? 0 : Number(formValues.DC) || 0,
        Pallet: formValues.Pallet === "" ? 0 : Number(formValues.Pallet) || 0,
        Stapel: formValues.Stapel === "" ? 0 : Number(formValues.Stapel) || 0,
        Wpkkar: formValues.Wpkkar === "" ? 0 : Number(formValues.Wpkkar) || 0,
      })
    );
  
    // ✅ foto meesturen (optioneel)
    if (selectedPhoto) {
      fd.append("photo", selectedPhoto);
    }

    // 🔴 NEGATIEVE SALDO CHECK
    for (const key of ["DC", "Pallet", "Stapel", "Wpkkar"]) {
      const current = Number(currentBalance[key] || 0);
      const pending = Number(pendingTotals?.[key] || 0);
      const input = Number(formValues[key] || 0);

      // wat blijft er over na deze actie?
      const remaining = current - pending - input;

      if (remaining < 0) {
        alert(
          `Te hoge invoer voor ${key} ❌

    Huidig saldo: ${current}
    In behandeling: ${pending}
    Invoer: ${input}

    Resultaat zou ${remaining} zijn (onder 0 niet toegestaan)`
        );
        return; // ⛔ stop submit
      }
    }
  
    try {
      setSubmitting(true);
  
      await axios.post(`${API}/api/fust/pending`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
  
      const sentValues = Object.entries(formValues)
        .map(([key, value]) => [key, value === "" ? 0 : Number(value)])
        .filter(([_, v]) => v !== 0)
        .map(([k, v]) => `${k}: ${v}`)
        .join("\n");

      alert(`Melding verzonden voor ${modalCompany.Name} ✅

      ${sentValues}

      Wacht op goedkeuring door magazijn.`);
      closeModal();
    } catch (e) {
      console.error("❌ Indienen mislukt:", e?.response?.data || e.message);
      alert("Indienen mislukt. Probeer opnieuw.");
      setSubmitting(false);
    }
  };

  return (
    <div className="transport-fust">
      <TransportNavbar />
      <h1>📦 Fustoverzicht</h1>

      {loading && <p>Laden...</p>}
      {error && <p className="error">⚠️ {error}</p>}
      {Object.keys(groupedCompanies).length === 0 && !loading && (
        <p>Geen fustgegevens beschikbaar.</p>
      )}

      <ul className="region-list">
        {Object.keys(groupedCompanies)
          .sort((a, b) => {
            if (a === "Onbekend") return 1; // "Onbekend" naar beneden
            if (b === "Onbekend") return -1; // idem
            return a.localeCompare(b);
          })
          .map((region) => (
            <li key={region}>
              <button className="region-button" onClick={() => toggleRegion(region)}>
                {expandedRegion === region ? "▼" : "▶"} {region}
              </button>

              {expandedRegion === region && (
                <ul className="company-list">
                  {groupedCompanies[region].map((company) => {
                    const locationKey = company._locationKey || company.Companyid;

                    const isOpen =
                      expandedCompanyByRegion[region] === locationKey;

                    return (
                      <li key={locationKey} className="company-item">
                        <button
                          className="company-button"
                          onClick={() => toggleCompany(region, locationKey)}
                        >
                          {isOpen ? "▼" : "▶"}{" "}
                          {company._locationType === "shipping"
                            ? company._companyName
                            : company.Name}{" "}
                          - {company.Address} {company.City}
                        </button>

                        {isOpen && (
                          <div className="company-fust">
                            {company._locationType === "shipping" && (
                              <div className="location-info">
                                <p>
                                  <strong>Locatie:</strong>{" "}
                                  {company._shippingName || company.ShippingAddresses?.[0]?.Name}
                                </p>

                                <p>
                                  <strong>Shipping code:</strong> {company._shippingCode}
                                </p>
                              </div>
                            )}

                            <p>DC: {company.DC}</p>
                            <p>Pallet: {company.Pallet}</p>
                            <p>Stapel: {company.Stapel}</p>
                            <p>Wpkkar: {company.Wpkkar}</p>

                            <button
                              className="return-button"
                              onClick={() => openReturnModal(company)}
                            >
                              Fusten melden
                            </button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          ))}
      </ul>

      {isModalOpen && modalCompany && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Fusten melden — {modalCompany.Name}</h2>

            {/* Als er shipping addresses zijn: kies er één */}
            {Array.isArray(modalCompany.ShippingAddresses) &&
              modalCompany.ShippingAddresses.length > 0 && (
                <div className="modal-row">
                  <label>Shipping address</label>
                  <select
                    value={modalSelectedCode || ""}
                    onChange={(e) => setModalSelectedCode(e.target.value)}
                  >
                    {modalCompany.ShippingAddresses.map((addr) => (
                      <option key={addr.Code} value={addr.Code}>
                        {addr.Code} — {addr.Name} — {addr.Address} {addr.City}
                      </option>
                    ))}
                  </select>
                </div>
              )}

            {pendingLoading && (
              <p style={{ fontSize: "0.85em", marginTop: "8px" }}>
                Pending wordt geladen…
              </p>
            )}
            <div className="fust-rows">
              {["DC", "Pallet", "Stapel", "Wpkkar"].map((key) => {
                const pending =
                  pendingTotals && typeof pendingTotals[key] === "number"
                    ? pendingTotals[key]
                    : 0;

                return (
                  <div key={key} className="fust-row">
                    <div className="fust-label">
                      <strong>{key}</strong>{" "}
                      <span>(saldo: {currentBalance[key]})</span>
                      {pending !== 0 && (
                        <span
                          style={{
                            display: "block",
                            fontSize: "0.85em",
                            color: "#b45309",
                            marginTop: 2,
                          }}
                        >
                          {pending > 0
                            ? `in behandeling: -${pending}` 
                            : `in behandeling: +${Math.abs(pending)}`}  
                        </span>
                      )}
                    </div>
                    <input
                      type="number"
                      step="1"
                      placeholder="0"
                      value={formValues[key]}
                      onChange={(e) => onChangeQty(key, e.target.value)}
                    />
                  </div>
                );
              })}
            </div>
            
            <div className="fust-row">
              <div className="fust-label">
                <strong>Foto</strong>
                <span>(optioneel)</span>
              </div>  
              <label className="upload-btn">
                
                📷 Foto maken / uploaden
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: "none" }}
                  onChange={(e) => onPhotoSelect(e.target.files[0])}
                />
              </label>

              {photoPreview && (
                <div style={{ marginTop: "10px" }}>
                  <img
                    src={photoPreview}
                    alt="Preview"
                    style={{
                      width: "100%",
                      maxHeight: "200px",
                      objectFit: "contain",
                      borderRadius: "6px",
                      border: "1px solid #ddd",
                    }}
                  />
                </div>
              )}
            </div>
                    
            <div className="modal-actions">
              <button className="btn-cancel" onClick={closeModal} disabled={submitting}>
                Sluiten
              </button>
              <button className="btn-confirm" onClick={confirmReturn} disabled={submitting}>
                {submitting ? "Verzenden…" : "Bevestigen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransportFust;
